/**
 * Drawer de navigation mobile, construit sur <details> natif.
 *
 * Le JS complète le comportement natif : fermeture par Échap et par clic sur
 * le fond assombri, piège de focus dans le panneau (Tab boucle), synchronise
 * aria-expanded et l'étiquette du bouton.
 */
class HeaderDrawer extends HTMLElement {
  #details = null;
  #summary = null;

  connectedCallback() {
    this.#details = this.querySelector('details');
    this.#summary = this.querySelector('summary');
    if (!this.#details || !this.#summary) return;

    this.#details.addEventListener('toggle', () => {
      this.#summary.setAttribute('aria-expanded', this.#details.open ? 'true' : 'false');
    });

    this.addEventListener('keydown', (event) => {
      if (!this.#details.open) return;

      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'Tab') this.#trapFocus(event);
    });

    // Clic sur le fond assombri (la zone du <details> hors du panneau et hors du bouton)
    this.#details.addEventListener('click', (event) => {
      if (event.target !== this.#details) return;
      this.close();
    });

    // Referme le drawer avant de naviguer (retour arrière du navigateur inclus)
    window.addEventListener('pageshow', () => this.close());
  }

  close() {
    if (!this.#details.open) return;
    this.#details.open = false;
    this.#summary.focus();
  }

  #trapFocus(event) {
    // On ne garde que les éléments RÉELLEMENT visibles : un sélecteur seul
    // capture aussi des éléments cachés (ex. le bouton de secours
    // .localization__submit mis `hidden` par JS), ce qui fausserait les bornes
    // du piège (getClientRects().length === 0 pour un élément display:none).
    const focusable = Array.from(
      this.#details.querySelectorAll(
        'summary, a[href], button:not([disabled]), select, input:not([type="hidden"])'
      )
    ).filter((el) => el.getClientRects().length > 0);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
